import React, { ClipboardEvent, JSX, useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { IcoCloseDeleteRemoveFill } from '../assets';
import MDSChip from './Chip';
import { listSliceData } from './utils';
import MDSTextField, { ITextFieldProps } from './TextField';

const chipWithTextFieldUseStyles = makeStyles(() => ({
  chipItem: {
    marginRight: '4px',
    marginTop: '8px',
    maxWidth: '100%',
  },
}));

interface IOwnProps {
  chips: string[];
  placeholder?: string;
  onChange: (data: string[], action: string, value: string) => boolean | void;
}

type IProps = IOwnProps & Omit<ITextFieldProps, 'onChange' | 'ref'>;

const TextFieldWithChip = (props: IProps): JSX.Element => {
  const classes = chipWithTextFieldUseStyles();

  const { chips, placeholder, onChange, ...restProps } = props;
  const [inputValue, setInputValue] = useState<string>('');

  const addChip = (chip: string) => {
    const newChip = chip.trim();
    if (!newChip || chips.indexOf(newChip) !== -1) return;

    const newChips = [...chips, newChip];
    onChange(newChips, 'add', '');
    setInputValue('');
  };

  const handleTextFieldChange = (_: unknown, value: unknown) => {
    setInputValue(value as string);
  };

  const handleChipAddKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key !== 'Enter') return;

    addChip(inputValue);
  };

  const handlePaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const clipboardText = e.clipboardData?.getData('text/plain');

    if (!clipboardText.trim()) return;

    const textArray = clipboardText
      .replaceAll('\t', '')
      .split(/[\r\n]+/g)
      .map((text) => text.trim())
      .filter((text) => text);

    const newChips = [...new Set([...chips, ...textArray])];
    onChange(newChips, 'add', '');
    setInputValue('');
  };

  const handlePlusClick = () => {
    addChip(inputValue);
  };

  const handleChipRemove = (value: string) => {
    if (value !== '') {
      onChange(listSliceData([...chips], value), 'remove', value);
    }
  };

  return (
    <Box width="100%">
      <MDSTextField
        size="large"
        value={inputValue}
        onChange={handleTextFieldChange}
        placeholder={placeholder}
        plusButton
        onPlus={handlePlusClick}
        onKeyDown={handleChipAddKeydown}
        onPaste={handlePaste}
        {...restProps}
      />
      <Box>
        {chips.map((item: string) => (
          <MDSChip
            classes={{
              root: classes.chipItem,
            }}
            key={item}
            variant="tint"
            size="small"
            color="bluegray"
            label={item}
            endIcon={<IcoCloseDeleteRemoveFill />}
            endIconClick={() => handleChipRemove(item)}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TextFieldWithChip;
